import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import { ArrowUpRight, Lock } from "lucide-react";

export default function SignUp() {
  const { signUp } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "parent",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast("Please choose a password with at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      const u = await signUp({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      });
      nav(
        u.role === "admin"
          ? "/workspace"
          : u.role === "caregiver"
          ? "/caregiver"
          : "/home",
        { replace: true }
      );
    } catch (err) {
      toast(err.response?.data?.detail || "We couldn't create your Alouvya just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <aside
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/5b7539fb-c408-4336-acd4-f414390d8b8e/images/9146ea81daa3fede3f87c81ea21a29df8ca9a82bd24744a4cc5f94903fbc13e2.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#FBF9F6]/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <span className="eyebrow text-[#2C2B29]/70">A new thread begins</span>
          <p className="mt-6 font-serif text-3xl italic text-[#2C2B29] leading-snug max-w-md">
            &ldquo;Trust is not a feature. It&rsquo;s the room everything else
            lives in.&rdquo;
          </p>
        </div>
      </aside>

      <main className="flex flex-col">
        <div className="px-6 sm:px-12 py-8 flex items-center justify-between">
          <Link
            to="/"
            data-testid="signup-brand"
            className="flex items-center"
          >
            <Brand size="sm" dataTestId="brand-signup" />
          </Link>
          <span className="trust-pill">
            <Lock size={11} strokeWidth={1.5} />
            Encrypted
          </span>
        </div>

        <div className="flex-1 flex items-center px-6 sm:px-16 py-10">
          <div className="w-full max-w-md fade-up">
            <span className="eyebrow">{t("auth.signup.eyebrow")}</span>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl leading-none tracking-tight">
              {t("auth.signup.title_line1")}
              <br />
              <em className="italic text-[#8A9984]">{t("auth.signup.title_italic")}</em>
            </h1>

            <form onSubmit={onSubmit} className="mt-12 space-y-7">
              <div>
                <label htmlFor="signup-name" className="eyebrow">{t("auth.name")}</label>
                <input
                  id="signup-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="orbit-input mt-3"
                  placeholder={t("auth.name_placeholder")}
                  data-testid="signup-name"
                />
              </div>
              <div>
                <label htmlFor="signup-email" className="eyebrow">{t("auth.email")}</label>
                <input
                  id="signup-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className="orbit-input mt-3"
                  placeholder={t("auth.email_placeholder")}
                  data-testid="signup-email"
                />
              </div>
              <div>
                <label htmlFor="signup-password" className="eyebrow">{t("auth.password")}</label>
                <input
                  id="signup-password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  className="orbit-input mt-3"
                  placeholder={t("auth.password_placeholder_signup")}
                  data-testid="signup-password"
                />
              </div>

              <fieldset className="border-0 p-0 m-0">
                <legend className="eyebrow">{t("auth.signup.role_label")}</legend>
                <div
                  role="radiogroup"
                  aria-label={t("auth.signup.role_label")}
                  className="mt-4 grid grid-cols-3 gap-2 sm:gap-3"
                >
                  {[
                    { r: "parent", label: t("auth.signup.role_parent"), note: t("auth.signup.role_parent_note") },
                    { r: "caregiver", label: t("auth.signup.role_caregiver"), note: t("auth.signup.role_caregiver_note") },
                    { r: "admin", label: t("auth.signup.role_admin"), note: t("auth.signup.role_admin_note") },
                  ].map(({ r, label, note }) => (
                    <button
                      type="button"
                      key={r}
                      role="radio"
                      aria-checked={form.role === r}
                      data-testid={`signup-role-${r}`}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`rounded-2xl border px-3 sm:px-5 py-4 text-left transition-all duration-300 ${
                        form.role === r
                          ? "bg-[#F2EFEA] border-[#9BA995]"
                          : "bg-white border-[#2C2B29]/10 hover:bg-[#FBF9F6]"
                      }`}
                    >
                      <div className="font-serif text-xl sm:text-2xl capitalize">{label}</div>
                      <div className="mt-1 text-[10px] sm:text-xs text-[#6A6863] tracking-wide leading-snug">
                        {note}
                      </div>
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="signup-submit"
                  className="btn-primary"
                >
                  {busy ? t("auth.submit_pending") : t("auth.signup.submit")}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
              </div>
            </form>

            <p className="mt-10 text-sm text-[#6A6863]">
              {t("auth.signup.back_to_signin")}{" "}
              <Link
                to="/signin"
                data-testid="signup-link-signin"
                className="text-[#2C2B29] underline underline-offset-4 decoration-[#9BA995]"
              >
                {t("auth.signup.signin_link")}
              </Link>
            </p>
          </div>
        </div>

        <div className="px-6 sm:px-12 py-8 text-xs text-[#6A6863] tracking-wide">
          You own your data. Always.
        </div>
      </main>
    </div>
  );
}
